import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'

/** หน้า Login — โครงสร้างและ class ตรงตาม login.html (HuayToday-ThemeBlue-V2) */
interface ModernLoginPageProps {
  themeConfig: TenantThemeConfig | null
}

const REMEMBER_ME_KEY = 'rememberMe'
const SAVED_USERNAME_KEY = 'savedUsername'

const ModernLoginPage: React.FC<ModernLoginPageProps> = ({ themeConfig }) => {
  const navigate = useNavigate()
  const [phone, setPhone] = useState(
    () => (typeof localStorage !== 'undefined' ? localStorage.getItem(SAVED_USERNAME_KEY) || '' : '')
  )
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [rememberMe, setRememberMe] = useState(
    () =>
      typeof localStorage !== 'undefined' &&
      (localStorage.getItem(REMEMBER_ME_KEY) !== 'false' || !!localStorage.getItem(SAVED_USERNAME_KEY))
  )

  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/', { replace: true })
    }
  }, [navigate])

  if (!themeConfig) {
    return (
      <div className="wrap_login d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="c_tertiary">กำลังโหลด...</div>
        <div className="bg_show" />
      </div>
    )
  }

  const logoUrl = themeConfig.theme?.branding?.logoUrl

  const handleLogin = async () => {
    const identifier = phone.trim()
    const secret = password

    if (!identifier || !secret || !themeConfig?.tenantId || submitting) {
      return
    }

    setSubmitting(true)
    try {
      const res = await authService.login({
        tenantId: themeConfig.tenantId,
        username: identifier,
        password: secret,
        rememberMe,
      })

      if (res.success) {
        if (typeof localStorage !== 'undefined') {
          if (rememberMe) {
            localStorage.setItem(SAVED_USERNAME_KEY, identifier)
          } else {
            localStorage.removeItem(SAVED_USERNAME_KEY)
          }
        }

        await Swal.fire({
          icon: 'success',
          title: 'เข้าสู่ระบบสำเร็จ',
          timer: 2000,
          showConfirmButton: false,
          timerProgressBar: true,
        })
        navigate('/dashboard', { replace: true })
        return
      }

      const msg =
        res.message ||
        res.error ||
        (res.code === 'CONCURRENT_LOGIN_BLOCKED' ? 'บัญชีนี้เข้าสู่ระบบจากอุปกรณ์อื่นอยู่' : 'เข้าสู่ระบบไม่สำเร็จ')

      await Swal.fire({
        icon: 'error',
        title: 'เข้าสู่ระบบไม่สำเร็จ',
        text: msg,
      })
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'เข้าสู่ระบบไม่สำเร็จ',
        text: err instanceof Error ? err.message : 'เกิดข้อผิดพลาด',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="wrap_login">
      <div className="container">
        <div className="">
          <div className="img_logo_l mb-3 mb-md-5">
            {logoUrl ? (
              <img src={logoUrl} alt={themeConfig.theme?.branding?.siteName || ''} />
            ) : (
              <span className="c_tertiary font_md">Logo</span>
            )}
          </div>

          <label htmlFor="login-identifier" className="c_tertiary fs-5">
            เบอร์โทร หรือ Username
          </label>

          <div className="form_inp">
            <div className="input_box border_10 mb-2">
              <div className="icon">
                <i className="fas fa-mobile-alt c_secondary"></i>
              </div>
              <input
                id="login-identifier"
                type="text"
                placeholder="เบอร์โทรศัพท์ หรือ Username"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="c_primary"
                style={{ color: '#0f172a' }}
              />
            </div>
          </div>

          <label htmlFor="login-password" className="c_tertiary fs-5">
            รหัสผ่าน
          </label>

          <div className="form_inp">
            <div className="input_box border_10 mb-2">
              <div className="icon">
                <i className="fas fa-lock c_secondary"></i>
              </div>
              <input
                id="login-password"
                type="password"
                placeholder="รหัสผ่าน"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    void handleLogin()
                  }
                }}
                className="c_primary"
                style={{ color: '#0f172a' }}
              />
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="d-flex align-items-center c_tertiary font_rg mb-0" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => {
                  const v = e.target.checked
                  setRememberMe(v)
                  if (typeof localStorage !== 'undefined') {
                    localStorage.setItem(REMEMBER_ME_KEY, v ? 'true' : 'false')
                  }
                }}
                className="me-2"
              />
              <span>จำฉันไว้ในระบบ</span>
            </label>
            <Link to="/forgot-password" className="font_rg c_tertiary text-decoration-none">
              ลืมรหัสผ่าน
            </Link>
          </div>

          <button
            type="button"
            className="link_pmr box_row justify-content-center font_rg bg_primary border_10 ins_sd text-decoration-none w-100 border-0"
            onClick={() => {
              void handleLogin()
            }}
            disabled={submitting || !phone.trim() || !password}
          >
            <span className="c_tertiary">{submitting ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}</span>
          </button>

          {themeConfig.settings?.lineLoginEnabled !== false && (
            <>
              <h6 className="c_tertiary text-center my-3">หรือ</h6>
              <a
                href="#"
                className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_10 ins_sd text-decoration-none"
                onClick={(e) => e.preventDefault()}
              >
                <span className="c_tertiary d-flex align-items-center">
                  <i className="fab fa-line fs-3 me-2"></i>
                  ลงชื่อเข้าใช้ด้วย LINE
                </span>
              </a>
            </>
          )}

          <h6 className="c_tertiary text-center my-3">คุณยังไม่มีบัญชีใช่หรือไม่?</h6>

          <Link
            to="/register"
            className="link_pmr box_row justify-content-center font_rg bg_gd_tertiary border_10 ins_sd text-decoration-none"
          >
            <div className="d-flex align-items-center ft_sd c_tertiary font_md">
              <i className="fas fa-user-plus me-2"></i>
              <span className="">สมัครสมาชิก</span>
            </div>
          </Link>
        </div>
      </div>

      <div className="bg_show"></div>
    </div>
  )
}

export default ModernLoginPage
